import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlans, setSelectedPlan as setSelectedPlanAction, clearError } from '../../features/payment/paymentSlice';
import paymentService from '../../services/paymentService';

// Load Outseta SDK dynamically
function loadOutsetaSDK() {
  if (document.getElementById('outseta-options-script')) return;

  const optionsScript = document.createElement('script');
  optionsScript.id = 'outseta-options-script';
  optionsScript.innerHTML = `
    var o_options = {
      domain: 'juco-pipeline-2.outseta.com',
      load: 'auth,customForm,emailList,leadCapture,nocode,profile,support'
    };
  `;
  document.head.appendChild(optionsScript);

  const sdkScript = document.createElement('script');
  sdkScript.id = 'outseta-sdk-script';
  sdkScript.src = 'https://cdn.outseta.com/outseta.min.js';
  sdkScript.setAttribute('data-options', 'o_options');
  document.head.appendChild(sdkScript);
}

// Get a base name by stripping interval-related words
function getBasePlanName(name) {
  return name
    .replace(/\b(monthly|yearly|annual|month|year)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const PlansSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const registrationData = location.state?.registrationData;
  const userRole = location.state?.role || 'coach';

  const { plans, loading, error } = useSelector((state) => state.payment);
  const [localSelectedPlan, setLocalSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('year'); // default to yearly
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Plans to show: those tagged for the current role (as before) PLUS these two
  // specific seat-based packages.
  const EXTRA_PLAN_NAMES = ['staff package', 'program package'];
  const filteredPlans = plans.filter((plan) => {
    const role = userRole.toLowerCase();
    const name = (plan.name || '').toLowerCase().trim();
    const idMatch = (plan.planId || plan._id || '').toLowerCase().includes(role);
    const roleMatch = name.includes(role) || idMatch;
    const isExtra = EXTRA_PLAN_NAMES.includes(name);
    return roleMatch || isExtra;
  });

  // Group plans by base name so monthly + yearly become one card
  const groupedPlans = useMemo(() => {
    const groups = {};
    filteredPlans.forEach((plan) => {
      const baseName = getBasePlanName(plan.name);
      if (!groups[baseName]) {
        groups[baseName] = { baseName, monthly: null, yearly: null };
      }
      const interval = (plan.interval || 'month').toLowerCase();
      if (interval === 'year' || interval === 'yearly' || interval === 'annual') {
        groups[baseName].yearly = plan;
      } else {
        groups[baseName].monthly = plan;
      }
    });
    return Object.values(groups);
  }, [filteredPlans]);

  useEffect(() => {
    if (!registrationData) {
      const backRoute = userRole === 'scout' ? '/auth/register-scout' : '/auth/register-coach';
      navigate(backRoute);
      return;
    }

    dispatch(fetchPlans(userRole));
    loadOutsetaSDK();

    return () => {
      dispatch(clearError());
    };
  }, [registrationData, navigate, userRole, dispatch]);

  // When billing interval changes, update selected plan to match
  useEffect(() => {
    if (localSelectedPlan) {
      const baseName = getBasePlanName(localSelectedPlan.name);
      const group = groupedPlans.find((g) => g.baseName === baseName);
      if (group) {
        const newPlan = billingInterval === 'year' ? group.yearly : group.monthly;
        if (newPlan && newPlan.planId !== localSelectedPlan.planId) {
          setLocalSelectedPlan(newPlan);
          dispatch(setSelectedPlanAction(newPlan));
        }
      }
    }
  }, [billingInterval]);

  const handlePlanSelect = (group) => {
    const plan = billingInterval === 'year' ? (group.yearly || group.monthly) : (group.monthly || group.yearly);
    setLocalSelectedPlan(plan);
    dispatch(setSelectedPlanAction(plan));
  };

  const handleContinue = async () => {
    if (!localSelectedPlan) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Create FormData for register-with-payment API
      const formData = new FormData();

      // Add registration data
      formData.append('firstName', registrationData.firstName);
      formData.append('lastName', registrationData.lastName);
      formData.append('email', registrationData.email);
      formData.append('password', registrationData.password);
      formData.append('role', userRole);
      formData.append('state', registrationData.state);

      // Role-specific fields
      if (userRole === 'coach') {
        if (registrationData.school) formData.append('school', registrationData.school);
        if (registrationData.division) formData.append('division', registrationData.division);
        if (registrationData.conference) formData.append('conference', registrationData.conference);
        if (registrationData.position) formData.append('position', registrationData.position);
      } else if (userRole === 'scout') {
        if (registrationData.teamName) formData.append('teamName', registrationData.teamName);
        if (registrationData.jobTitle) formData.append('jobTitle', registrationData.jobTitle);
      }

      // Add payment data
      formData.append('paymentProvider', 'outseta');
      formData.append('plan', localSelectedPlan.planId);

      // Hit the register-with-payment API
      const response = await paymentService.registerWithPayment(formData);
      console.log('Register with payment API response:', response);

      // Save pendingRegistrationId to localStorage for after payment
      if (response.pendingRegistrationId) {
        localStorage.setItem('pendingRegistrationId', response.pendingRegistrationId);
      }

      // Redirect to Outseta checkout URL from API response
      if (response.outsetaCheckoutUrl) {
        window.location.href = response.outsetaCheckoutUrl;
      }
    } catch (err) {
      console.error('Register with payment API failed:', err);
      setSubmitError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if the given group is currently selected
  const isGroupSelected = (group) => {
    if (!localSelectedPlan) return false;
    return group.monthly?.planId === localSelectedPlan.planId || group.yearly?.planId === localSelectedPlan.planId;
  };

  // Get the active plan from a group based on billing interval
  const getActivePlan = (group) => {
    return billingInterval === 'year' ? (group.yearly || group.monthly) : (group.monthly || group.yearly);
  };

  // Merge features from both plans (unique)
  const getMergedFeatures = (group) => {
    const features = new Set();
    group.monthly?.features?.forEach((f) => features.add(f));
    group.yearly?.features?.forEach((f) => features.add(f));
    return [...features];
  };

  // Get description (prefer the active plan's description)
  const getDescription = (group) => {
    const activePlan = getActivePlan(group);
    return activePlan?.description || group.monthly?.description || group.yearly?.description;
  };

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            Step 2
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            CHOOSE YOUR PLAN
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            Select the plan that best fits your needs.
          </p>
        </div>

        <div className="max-w-6xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          {/* Error Message */}
          {(error || submitError) && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm text-center">
              {error || submitError}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="py-12 text-center text-jp-muted">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-jp-accent mb-4"></div>
              <p>Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Plans Grid */}
              <div role="radiogroup" aria-label="Plans" className="rounded-xl p-5 sm:p-8 bg-jp-section border border-jp-outline/50 flex flex-col md:flex-row md:flex-wrap justify-center gap-6">
                {groupedPlans.length === 0 ? (
                  <div className="w-full py-6 text-center text-jp-muted">
                    No plans available for your role. Please contact support.
                  </div>
                ) : (
                  groupedPlans.map((group) => {
                    const activePlan = getActivePlan(group);
                    const selected = isGroupSelected(group);
                    const features = getMergedFeatures(group);
                    const description = getDescription(group);

                    return (
                      <div
                        key={group.baseName}
                        role="radio"
                        tabIndex={0}
                        aria-checked={selected}
                        onClick={() => handlePlanSelect(group)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePlanSelect(group);
                          }
                        }}
                        className={`relative flex flex-col cursor-pointer w-full md:max-w-md md:flex-1 text-left rounded-2xl p-6 sm:p-8 bg-jp-band text-white border-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-card ${
                          selected
                            ? 'border-cyan-400 shadow-lg shadow-cyan-500/25'
                            : 'border-transparent hover:border-cyan-400/50'
                        }`}
                      >
                        {/* Selected Badge */}
                        {selected && (
                          <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-cyan-400 text-slate-900 text-xs font-bold uppercase tracking-wider px-3 py-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Selected
                          </span>
                        )}

                        {/* Plan Name */}
                        <span className="block text-slate-300 text-sm font-bold uppercase tracking-wider mb-2 pr-28">
                          {group.baseName}
                        </span>

                        {/* Active Price */}
                        <span className="block mb-6">
                          <span className="text-5xl font-extrabold text-cyan-400 tabular-nums">
                            ${activePlan?.price}
                          </span>
                          <span className="text-slate-300 text-sm"> / year</span>
                        </span>

                        {/* Plan Description */}
                        {description && (
                          <span
                            className="block mb-6 text-slate-300 text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: description }}
                          />
                        )}

                        {/* Plan Features */}
                        {features.length > 0 && (
                          <ul className="space-y-2.5">
                            {features.map((feature, index) => (
                              <li key={index} className="flex gap-2 text-slate-200 text-sm">
                                <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Continue Button */}
              <div className="max-w-md mx-auto pt-8">
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!localSelectedPlan || submitting}
                  className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-card active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing...' : 'Continue'}
                </button>
              </div>

              {/* Back Link */}
              <div className="pt-5 text-center text-sm sm:text-base">
                <button
                  type="button"
                  onClick={() => {
                    const backRoute = userRole === 'scout' ? '/auth/register-scout' : '/auth/register-coach';
                    navigate(backRoute);
                  }}
                  className="font-semibold text-jp-accent hover:underline focus:outline-none focus-visible:underline"
                >
                  Back to registration
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default PlansSelectionPage;
